import { Flags } from '@fullstory/client-api';
import { Action, ActionMenu, Box, Flex, Icon, Tooltip } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { ObjectType } from '@fullstory/proto/fs/services/dx-objects';
import { LibraryObjectType } from '@fullstory/routes';
import { omit } from 'lodash';
import React, { useMemo, useState } from 'react';
import { AccessDeniedMessage } from '../AccessDeniedMessage';
import {
  cannotFeatureObjectTypeMessage,
  noPermissionMessage,
  typeSupportsFeatureCard,
} from '../../utils';
import { RemoveObjectModal } from '../RemoveObjectModal';
import { LibraryObject } from './types';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { FGACButton, getFGACIconButtonExplorerRoleProps } from '../FGACButton';
import { Visibility } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';

const standardMenuActionIds = [
  'edit',
  'add',
  'feature',
  'duplicate',
  'transfer',
  'remove',
  'archive',
] as const;
type StandardMenuActionId = (typeof standardMenuActionIds)[number];

const dashboardMenuActionIds = [
  'edit',
  'add',
  'feature',
  'duplicate',
  'transfer',
  'remove',
  'delete',
] as const;
type DashboardMenuActionId = (typeof dashboardMenuActionIds)[number];

type MenuActionId = StandardMenuActionId | DashboardMenuActionId;

/**
 * Actions requiring a confirmation step.
 */
const CONFIRM_MENU_ACTION_IDS = ['archive', 'delete', 'remove'] as const;
type ConfirmMenuActionId = (typeof CONFIRM_MENU_ACTION_IDS)[number];

type MenuAction = Pick<Action, 'label' | 'iconName'> & {
  id: MenuActionId;
  /**
   * @default false
   */
  requiresCanOwn?: boolean;
  /**
   * @default false
   */
  disableWhenCanned?: boolean;
  requiredPermission?: Flag;
  requiresPublic?: boolean;
};

type NonDashboardLibraryObject = LibraryObject & {
  objectType: Exclude<LibraryObjectType, ObjectType.DASHBOARD>;
};

type DashboardLibraryObject = LibraryObject & {
  objectType: ObjectType.DASHBOARD;
};

const isDashboardLibraryObject = (
  libraryObject: LibraryObject,
): libraryObject is DashboardLibraryObject => libraryObject.objectType === ObjectType.DASHBOARD;
const isDashboardMenuAction = (id: string): id is DashboardMenuActionId =>
  dashboardMenuActionIds.includes(id as DashboardMenuActionId);
const isNonDashboardLibraryObject = (
  libraryObject: LibraryObject,
): libraryObject is NonDashboardLibraryObject => libraryObject.objectType !== ObjectType.DASHBOARD;
const isStandardMenuAction = (id: string): id is StandardMenuActionId =>
  standardMenuActionIds.includes(id as StandardMenuActionId);

const ADD_ACTION_ID = 'add';
const TRANSFER_ACTION_ID = 'transfer';
const FEATURE_ACTION_ID = 'feature';
const REMOVE_ACTION_ID = 'remove';
const DELETE_ACTION_ID = 'delete';

// maps guarantee menu actions for each possible union'd id
// keep in order for eventual rendering (actions are also filtered when the menu is made)
const SHARED_MENU_ACTIONS = {
  edit: {
    id: 'edit',
    label: intl.formatMessage({
      defaultMessage: 'Edit',
      id: 'BqgkJF',
      description: 'View detail action for a resource',
    }),
    iconName: 'pencil',
  },
  duplicate: {
    id: 'duplicate',
    label: intl.formatMessage({
      defaultMessage: 'Duplicate',
      id: 'Iyhr/6',
      description: 'Duplicate action for a resource',
    }),
    iconName: 'duplicate',
    requiredPermission: Flag.PermCreateLibraryObjects,
  },
  add: {
    id: ADD_ACTION_ID,
    label: intl.formatMessage({ defaultMessage: 'Add to a space...', id: '14OaEI' }),
    iconName: 'plus-circle',
    requiredPermission: Flag.PermModifySpaceObjects,
    requiresPublic: true,
  },
  feature: {
    id: FEATURE_ACTION_ID,
    label: intl.formatMessage({
      defaultMessage: 'Add to Featured...',
      id: '3pF6lb',
    }),
    iconName: 'grid',
    requiredPermission: Flag.PermModifySpaceObjects,
  },
  transfer: {
    id: 'transfer',
    label: intl.formatMessage({
      defaultMessage: 'Transfer...',
      id: '/bcxmw',
    }),
    iconName: 'arrow-curve',
    requiresCanOwn: true,
    requiredPermission: Flag.PermModifySpaceObjects,
    requiresPublic: true,
  },
  // This is only for removing Links, we do not support removing objects from a Space.
  remove: {
    id: REMOVE_ACTION_ID,
    label: intl.formatMessage({
      defaultMessage: 'Remove',
      id: 'G/yZLu',
    }),
    iconName: 'minus-circle',
    tooltip: intl.formatMessage({
      defaultMessage: 'Removes the link from this space.',
      id: 'md7vu3',
    }),
    requiredPermission: Flag.PermModifySpaceObjects,
  },
} as const;

const STANDARD_MENU_ACTION_MAP: { [id in StandardMenuActionId]: MenuAction } = {
  ...SHARED_MENU_ACTIONS,
  archive: {
    id: 'archive',
    label: intl.formatMessage({
      defaultMessage: 'Archive',
      id: 'dbPVuu',
      description: 'Archive action for a resource',
    }),
    iconName: 'trash',
    requiresCanOwn: true,
    disableWhenCanned: true,
    requiredPermission: Flag.PermModifyOwnLibraryObjects,
  },
};

const DASHBOARD_MENU_ACTION_MAP: { [id in DashboardMenuActionId]: MenuAction } = {
  ...SHARED_MENU_ACTIONS,
  delete: {
    id: DELETE_ACTION_ID,
    label: intl.formatMessage({
      defaultMessage: 'Delete',
      id: 'KPA3a8',
      description: 'Delete action for a resource',
    }),
    iconName: 'trash',
    requiresCanOwn: true,
    disableWhenCanned: true,
    requiredPermission: Flag.PermDeleteOwnLibraryObjects,
  },
};

const DASHBOARD_ACTIONS = Object.values(DASHBOARD_MENU_ACTION_MAP);
const STANDARD_ACTIONS = Object.values(STANDARD_MENU_ACTION_MAP);

const makeMenuActions = (
  object: LibraryObject,
  currentSpaceId: string | null | undefined,
  hasSpacesFeaturePack: boolean,
  flags: Flags,
): Action[] => {
  const isLinkedObject =
    !!currentSpaceId && !!object?.owningSpace && object.owningSpace.id !== currentSpaceId;

  const actions = isDashboardLibraryObject(object) ? DASHBOARD_ACTIONS : STANDARD_ACTIONS;

  // apply filtering and transformation to actions depending on the Space and object context
  return actions.reduce<Action[]>((acc, action) => {
    // map to an Action usable by the ActionMenu
    let translatedAction: Action = {
      ...omit(action, ['requiresCanOwn', 'disableWhenCanned']),
    };

    // permission takes precedence over all
    if (action.requiredPermission && !flags.hasBit(action.requiredPermission)) {
      translatedAction = {
        ...translatedAction,
        disabled: true,
        tooltip: <AccessDeniedMessage suggestion="none" viewOnly />,
      };
      // canned takes precedence over ownership
    } else if (action.disableWhenCanned && object.canned) {
      translatedAction = {
        ...translatedAction,
        disabled: true,
        tooltip: <AccessDeniedMessage suggestion="none" isCanned />,
      };
    } else if (action.requiresCanOwn && !object.canOwn) {
      translatedAction = {
        ...translatedAction,
        disabled: true,
        tooltip: <AccessDeniedMessage suggestion="none" />,
      };
    } else if (action.requiresPublic && object.visibility === Visibility.PRIVATE) {
      translatedAction = {
        ...translatedAction,
        disabled: true,
      };
    }

    if (action.id === FEATURE_ACTION_ID && currentSpaceId) {
      return acc.concat({
        ...translatedAction,
        // disable the feature action when its not supported for this particular object
        ...(!typeSupportsFeatureCard(object.objectType, flags)
          ? {
              disabled: true,
              tooltip: intl.formatMessage(cannotFeatureObjectTypeMessage, {
                objectType: object.objectType,
              }),
            }
          : {}),
      });
    }

    if (action.id === ADD_ACTION_ID && currentSpaceId) {
      // Remove the 'add' action when we are on a space library
      return acc;
    }

    if (action.id === FEATURE_ACTION_ID && !currentSpaceId) {
      // Remove the 'feature' action when we are on the all Library
      return acc;
    }

    if (action.id === REMOVE_ACTION_ID && !isLinkedObject) {
      // omit remove when the object is owned and not a link/reference
      // the action for an owned object is to move it to a different Space
      return acc;
    }

    if (action.id === TRANSFER_ACTION_ID && !hasSpacesFeaturePack) {
      // omit the 'transfer' feature when the org doesn't have the feature pack
      return acc;
    }

    return acc.concat(translatedAction);
  }, []);
};

const REMOVE_OBJECT_MODAL_MODES: {
  [id in ConfirmMenuActionId]: React.ComponentProps<typeof RemoveObjectModal>['mode'];
} = {
  remove: 'remove',
  delete: 'delete-dashboard',
  archive: 'archive',
};

interface LibraryTableMenuActionsProps {
  object: LibraryObject;
  currentSpaceId: string | null | undefined;
  hasSpacesFeaturePack: boolean;
  canUnarchiveObject: boolean;
  flags: Flags;
  onEdit: (object: LibraryObject) => void;
  onAdd: (object: LibraryObject) => void;
  onFeature: (object: LibraryObject) => void;
  onDuplicate: (object: LibraryObject) => void;
  onTransfer: (object: LibraryObject) => void;
  onRemove: (object: LibraryObject) => void;
  onArchive: (object: NonDashboardLibraryObject) => void;
  onRestore: (object: NonDashboardLibraryObject) => void;
  onDeleteDashboard: (object: DashboardLibraryObject) => void;
  setActionMenuOpenObjectId: (id: string | null) => void;
}

// this is per object, but callbacks need to be for all objects
export const LibraryTableMenuActions: React.FC<LibraryTableMenuActionsProps> = ({
  object,
  currentSpaceId,
  hasSpacesFeaturePack,
  canUnarchiveObject,
  flags,
  onEdit,
  onAdd,
  onFeature,
  onDuplicate,
  onTransfer,
  onRemove,
  onArchive,
  onRestore,
  onDeleteDashboard,
  setActionMenuOpenObjectId,
}) => {
  const [confirmMenuActionId, setConfirmMenuActionId] = useState<ConfirmMenuActionId | null>(null);

  // this is for typing to guarantee a callback for each ActionMenu item's action
  const standardMenuActions: {
    [id in StandardMenuActionId]: (object: NonDashboardLibraryObject) => void;
  } = {
    edit: onEdit,
    add: onAdd,
    feature: onFeature,
    duplicate: onDuplicate,
    transfer: onTransfer,
    remove: onRemove,
    archive: onArchive,
  };
  const dashboardMenuActions: {
    [id in DashboardMenuActionId]: (object: DashboardLibraryObject) => void;
  } = {
    edit: onEdit,
    add: onAdd,
    feature: onFeature,
    duplicate: onDuplicate,
    transfer: onTransfer,
    remove: onRemove,
    delete: onDeleteDashboard,
  };

  const showArchived = object.archived && isNonDashboardLibraryObject(object);
  const menuActions = showArchived
    ? []
    : makeMenuActions(object, currentSpaceId, hasSpacesFeaturePack, flags);

  const handleMenuAction = (id: MenuActionId) => {
    if (isDashboardMenuAction(id) && isDashboardLibraryObject(object)) {
      dashboardMenuActions[id](object);
    } else if (isStandardMenuAction(id) && isNonDashboardLibraryObject(object)) {
      standardMenuActions[id](object);
    }
  };

  const handleConfirmAction = () => {
    if (confirmMenuActionId) {
      handleMenuAction(confirmMenuActionId);
      setConfirmMenuActionId(null);
    }
  };

  const isFeatured = useMemo(
    () => !!currentSpaceId && object.featuredIn.some(s => s.id === currentSpaceId),
    [currentSpaceId, object.featuredIn],
  );

  return (
    <>
      <div data-component="LibraryTableMenuActions">
        {showArchived ? (
          <FGACButton
            testId="archive-icon-button"
            title={intl.formatMessage({
              defaultMessage: 'Restore',
              id: 'jE3mmK',
              description: 'Restore action for a resource',
            })}
            iconName="folder"
            onClick={e => {
              e.stopPropagation();
              // TS cant follow showArchived above, need to do this again
              if (isNonDashboardLibraryObject(object)) onRestore(object);
            }}
            tooltip={intl.formatMessage(noPermissionMessage)}
            disabled={!canUnarchiveObject}
            {...getFGACIconButtonExplorerRoleProps(canUnarchiveObject)}
          />
        ) : (
          <Flex gap={24} justifyContent="flex-end">
            {!!currentSpaceId && object.featuredIn.some(s => s.id === currentSpaceId) && (
              <Tooltip
                tooltipContent={intl.formatMessage(
                  {
                    defaultMessage:
                      "This {objectType, select, 1 {metric} 2 {funnel} 3 {retention} 5 {segment} 6 {dashboard} 7 {heatmap} 8 {journey} other {item}} is displayed on this space's Featured tab.",
                    id: 'ldkosk',
                  },
                  { objectType: object.objectType },
                )}
              >
                <Icon name="grid" color="secondary" size={16} />
              </Tooltip>
            )}
            {/* stop event propagation to prevent a row click and view action */}
            <Box onClick={e => e.stopPropagation()}>
              <ActionMenu
                data-component="LibraryTableMenuActions"
                actions={menuActions}
                onSelect={id => {
                  const menuAction = menuActions.find(a => a.id === id);

                  if (!menuAction || menuAction.disabled) return;

                  if (CONFIRM_MENU_ACTION_IDS.includes(menuAction.id as ConfirmMenuActionId)) {
                    setConfirmMenuActionId(menuAction.id as ConfirmMenuActionId);
                  } else {
                    // the below cast is guaranteed, if ActionMenu supported a generic this would not be needed
                    handleMenuAction(menuAction.id as MenuActionId);
                  }
                }}
                onOpenChanged={open => {
                  if (open) {
                    setActionMenuOpenObjectId(object.id);
                  } else {
                    setActionMenuOpenObjectId(null);
                  }
                }}
              />
            </Box>
          </Flex>
        )}
      </div>
      {/* stop event propagation to prevent a row click and view action */}
      <Box onClick={e => e.stopPropagation()}>
        <RemoveObjectModal
          open={!!confirmMenuActionId}
          onRemove={handleConfirmAction}
          onCloseSignal={() => setConfirmMenuActionId(null)}
          mode={REMOVE_OBJECT_MODAL_MODES[confirmMenuActionId ?? 'remove']}
          objectType={object.objectType}
          objectLastUpdated={object.lastUpdated}
          objectLastUpdatedBy={object.lastUpdatedBy}
          isFeaturedInCurrentSpaceContext={isFeatured}
        />
      </Box>
    </>
  );
};
